import { CircleCheck, CloudUpload, Loader2, X } from "lucide-react";
import { type FileWithPath, useDropzone } from "react-dropzone";
import { Button } from "../ui/button";
import { cn } from "@/lib/utils";

export const PoAssetsDropzone = ({
  name,
  files,
  onDrop,
  maxFiles,
  errors,
  className,
  removeFile,
  isLoading,
  hasFileRemoveButton,
}: {
  name?: string;
  maxFiles?: number;
  errors?: string[];
  onDrop: (acceptedFiles: FileWithPath[]) => void;
  files?: { id: string; url: string }[];
  className?: string;
  removeFile?: (index: number) => void;
  isLoading?: boolean;
  hasFileRemoveButton?: boolean;
}) => {
  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    maxFiles,
    maxSize: 10 * 1024 * 1024,
    accept: {
      ["application/pdf"]: [".pdf"],
    },
  });

  return (
    <div>
      <section
        className={cn(
          "border-2 rounded border-primary border-dashed bg-gray-50 border-[#E4E4E7] outline-none transition duration-200 ease-in-out",
          files && files.length > 0 ? "cursor-default" : "cursor-pointer",
          className,
        )}
        {...(!files?.length ? getRootProps() : {})}
      >
        <div className="w-full flex flex-col items-center">
          {!files?.length && <input {...getInputProps()} />}
          <div className="flex flex-col text-center items-center justify-center gap-1 h-full py-4">
            <Button
              type="button"
              size="icon"
              variant="outline"
              className="rounded p-2 bg-slate-50"
            >
              {isLoading ? (
                <Loader2 className="w-8 h-8 animate-spin" />
              ) : files?.length ? (
                <CircleCheck className="w-8 h-8 text-bold" />
              ) : (
                <CloudUpload className="w-8 h-8" />
              )}
            </Button>
            <p className="text-muted-foreground font-medium">
              {isLoading
                ? "Carregando..."
                : files?.length
                  ? "Arquivo carregado"
                  : "Solte aqui"}
            </p>
            {files && files.length > 0 ? (
              <div className="flex items-center gap-2">
                <div className="max-w-64 flex items-center gap-2 py-2 px-4 bg-green-50 border border-green-200 rounded-md">
                  <p className="text-sm text-muted-foreground overflow-hidden text-ellipsis whitespace-nowrap">
                    {files[0]?.url.split("/").pop() || "Documento"}
                  </p>
                </div>
                {hasFileRemoveButton && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="rounded p-2"
                    onClick={() => removeFile?.(0)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
            ) : (
              <p className="self-stretch text-center justify-start text-muted-foreground text-xs leading-tight mx-2">
                Ou clique para enviar documentos. Tamanho máximo: 10 mb.
              </p>
            )}
          </div>
        </div>
      </section>

      {files && files.length > 0 && (
        <input type="hidden" name={name} value={files[0].id} />
      )}

      <p className="text-sm text-red-500">
        {errors && errors.length > 0 && errors[0]}
      </p>
    </div>
  );
};
